/**
 * Sidebar header component.
 *
 * Shows user info (or sign-in prompt) and settings menu when expanded.
 * Uses pure CSS transitions via data-sidebar-* attributes.
 */

import * as stylex from "@stylexjs/stylex";

import { Avatar, sidebar } from "@animus/primaris";

import type { AppSidebarUser } from "./types";
import { ComposedSettingsMenu } from "../settings-menu";
import { styles } from "./styles.stylex";

export interface AppSidebarHeaderProps {
  user: AppSidebarUser | null;
  onSignIn: (() => void) | undefined;
  onSignOut: (() => void) | undefined;
}

function UserSection({
  user,
  onSignIn,
}: Pick<AppSidebarHeaderProps, "user" | "onSignIn">) {
  if (user) {
    return (
      <div data-sidebar-user-profile {...stylex.props(styles.userProfile)}>
        <Avatar
          data-sidebar-avatar
          name={user.name}
          size="md"
          {...(user.avatarUrl ? { src: user.avatarUrl } : {})}
        />
        <div data-sidebar-user-info {...stylex.props(styles.userInfoContainer)}>
          <div {...stylex.props(styles.userInfo)}>
            <span {...stylex.props(styles.userName)}>{user.name}</span>
            {user.email && (
              <span {...stylex.props(styles.userEmail)}>{user.email}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (onSignIn) {
    return (
      <div data-sidebar-user-profile {...stylex.props(styles.userProfile)}>
        <div data-sidebar-avatar {...stylex.props(styles.signInPlaceholder)}>
          ?
        </div>
        <div data-sidebar-user-info {...stylex.props(styles.userInfoContainer)}>
          <button
            {...stylex.props(styles.signInButton)}
            onClick={onSignIn}
            type="button"
          >
            <div {...stylex.props(styles.signInInfo)}>
              <span {...stylex.props(styles.signInTitle)}>Sign in</span>
              <span {...stylex.props(styles.signInSubtitle)}>
                to unlock all features
              </span>
            </div>
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export function AppSidebarHeader({
  user,
  onSignIn,
  onSignOut,
}: AppSidebarHeaderProps) {
  return (
    <div data-sidebar-header {...stylex.props(sidebar.header)}>
      <div data-sidebar-header-row {...stylex.props(styles.headerRow)}>
        <UserSection onSignIn={onSignIn} user={user} />
        <div
          data-sidebar-settings
          data-sidebar-speed="2x"
          {...stylex.props(styles.settingsContainer)}
        >
          <ComposedSettingsMenu {...(user && onSignOut ? { onSignOut } : {})} />
        </div>
      </div>
    </div>
  );
}
